import SearchInput from '../../components/ui/SearchInput';
import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { toast } from '../../components/ui/notify';
import { Navigate, useNavigate } from 'react-router-dom';
import countryData from '../../utils/countory.json';
import {
  getCountryByCode,
  getDefaultPhoneCountryCode,
  getPhoneRuleFromCountry,
  validateNationalPhoneDigits,
  phoneLengthPlaceholder,
  phoneLengthHintSuffix,
  getCountryDisplayName,
} from '../../utils/phoneCountryRules';
import { validateEmail, validatePassword, validateName, validateConfirmPassword, validateForm, getPasswordRequirementState } from '../../utils/validation';
import {
  getRegistrationCourseCategoriesApi,
  newRegistrationApi,
} from '../../services/auth/authService';
import { useOrganizationBranding } from '../../hooks/useOrganizationBranding';
import { AuthOrgBrandingLogo } from '../../components/auth/AuthOrgBrandingLogo';
import { isFeedFeatureEnabled } from '../../utils/featureFlags';
import { getAuthApiErrorMessage } from './authApiError';

function RegisterForm() {
  const orgBranding = useOrganizationBranding('Register');
  const navigate = useNavigate();
  const defaultPhoneCode = getDefaultPhoneCountryCode();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [selectedCountryCode, setSelectedCountryCode] = useState(defaultPhoneCode);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCountries, setFilteredCountries] = useState([]);
  const dropdownRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    letters: false,
    numbers: false,
  });
  const [errors, setErrors] = useState({});
  const [interestCategories, setInterestCategories] = useState([]);
  const [selectedInterestIds, setSelectedInterestIds] = useState([]);
  const [isLoadingInterests, setIsLoadingInterests] = useState(true);

  const [selectedCountry, setSelectedCountry] = useState(
    () => getCountryByCode(defaultPhoneCode) || getCountryByCode('+91'),
  );
  const phoneRule = getPhoneRuleFromCountry(selectedCountry);

  const validatePhoneByCountryCode = (phone, countryCode) =>
    validateNationalPhoneDigits(phone, countryCode, { required: true });

  // Filter countries based on search query
  useEffect(() => {
    // Filter countries based on search query
    if (searchQuery.trim() === '') {
      setFilteredCountries(countryData.countoryCode);
    } else {
      const filtered = countryData.countoryCode.filter(country =>
        country.country.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredCountries(filtered);
    }
  }, [searchQuery]);

  // Handle Click Outside
  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Initialize filtered countries
  useEffect(() => {
    setFilteredCountries(countryData.countoryCode);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadInterestCategories = async () => {
      try {
        const response = await getRegistrationCourseCategoriesApi({
          organization_url: window.location.origin,
        });
        if (!cancelled) {
          setInterestCategories(response?.data?.categories || []);
        }
      } catch (error) {
        if (!cancelled) setInterestCategories([]);
      } finally {
        if (!cancelled) setIsLoadingInterests(false);
      }
    };

    loadInterestCategories();
    return () => {
      cancelled = true;
    };
  }, []);

  // Validation Rules
  const validationRules = {
    name: validateName,
    email: validateEmail,
    phone: (value) => validatePhoneByCountryCode(value, selectedCountryCode),
    password: validatePassword,
    confirmPassword: validateConfirmPassword
  };

  // Check password requirements
  const checkPasswordRequirements = (password) => {
    const requirements = getPasswordRequirementState(password);
    return {
      length: requirements.length,
      letters: requirements.letter,
      numbers: requirements.number,
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue =
      name === 'phone'
        ? value.replace(/\D/g, '').slice(0, phoneRule.max)
        : value;

    // Update both form data and clear errors in a single function
    const newFormData = { ...formData, [name]: nextValue };
    const newErrors = { ...errors, [name]: undefined };

    // Update password requirements if password field changes
    if (name === 'password') {
      setPasswordRequirements(checkPasswordRequirements(nextValue));
      // Re-validate confirm when password changes; clear stale "do not match" when they align
      if (newFormData.confirmPassword) {
        const confirmPasswordErrors = validateConfirmPassword(newFormData.confirmPassword, nextValue);
        newErrors.confirmPassword =
          confirmPasswordErrors.length > 0 ? confirmPasswordErrors : undefined;
      }
    }

    if (name === 'confirmPassword' && newFormData.password) {
      const confirmPasswordErrors = validateConfirmPassword(nextValue, newFormData.password);
      newErrors.confirmPassword =
        confirmPasswordErrors.length > 0 ? confirmPasswordErrors : undefined;
    }

    setFormData(newFormData);
    setErrors(newErrors);
  };

  const handleCountrySelect = (country) => {
    setSelectedCountryCode(country.code);
    setSelectedCountry(country);
    const nextRule = getPhoneRuleFromCountry(country);
    setFormData((prev) => ({
      ...prev,
      phone: prev.phone.slice(0, nextRule.max),
    }));
    setErrors((prev) => ({ ...prev, phone: undefined }));
    setShowCountryDropdown(false);
  };

  const toggleInterest = (categoryId) => {
    const normalizedId = String(categoryId);
    setSelectedInterestIds((current) =>
      current.includes(normalizedId)
        ? current.filter((id) => id !== normalizedId)
        : [...current, normalizedId]
    );
    setErrors((current) => ({ ...current, interests: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validation = validateForm(formData, validationRules);
    const interestError = interestCategories.length > 0 && selectedInterestIds.length === 0;
    if (!validation.isValid || interestError) {
      setErrors({
        ...validation.errors,
        ...(interestError ? { interests: ['Select at least one interest.'] } : {}),
      });
      return;
    }

    if (validation.isValid) {
      setIsLoading(true);
      const loaderStart = Date.now();
      const organization_url = window.location.origin;
      const registrationData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        countryCode: selectedCountryCode,
        organization_url,
        preferred_category_ids: selectedInterestIds.map(Number),
      };

      try {
        const response = await newRegistrationApi(registrationData);
        let elapsed = Date.now() - loaderStart;
        if (elapsed < 800) await new Promise(r => setTimeout(r, 800 - elapsed));

        if (response.success) {
          sessionStorage.setItem('registrationData', JSON.stringify(registrationData));
          const msg = response.data?.message ?? response.message;
          if (msg) toast.success(msg, {
            position: "top-center",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            onClose: () => {
              setIsLoading(false);
              navigate('/auth/register-otp', { state: { email: formData.email } });
            }
          });
        }
      } catch (error) {
        console.error("[Register] API error:", error?.response?.data ?? error);
        let elapsed = Date.now() - loaderStart;
        if (elapsed < 800) await new Promise(r => setTimeout(r, 800 - elapsed));
        toast.error(getAuthApiErrorMessage(error), {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        setIsLoading(false);
      }
    } else {
      setErrors(validation.errors);
    }
  };

  return (
    <div className={"max-[480px]:tw-p-4 [[data-theme='dark']_&]:tw-bg-[#1a1b1e] tw-min-h-[100dvh] tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-[2rem_1rem] tw-bg-[#F5F7FF] [background:radial-gradient(ellipse_at_20%_0%,_#e8edff_0%,_transparent_45%),_#f5f7fb] auth-container"}>
      <div className={"max-[480px]:tw-p-6 [[data-theme='dark']_&]:tw-bg-[#25262b] [[data-theme='dark']_&]:tw-shadow-[0px_4px_20px_rgba(0,_0,_0,_0.15)] tw-relative tw-bg-[#FFFFFF] tw-rounded-[1.25rem] tw-p-10 tw-w-full tw-max-w-[28.75rem] tw-shadow-[0_1rem_3rem_-1.5rem_rgb(30_41_59_/_.25)] tw-border-[1px] tw-border-solid tw-border-[#e2e8f0] max-[767px]:tw-p-[1.75rem_1.5rem] auth-card"}>
        <div className={"tw-text-center tw-mb-8 [&_h2]:tw-text-[#101828] [&_h2]:tw-text-[1.5625rem] [&_h2]:[font-weight:650] [&_h2]:tw-leading-[2rem] [&_h2]:tw-m-[0_0_.5rem_0] [&_h2]:tw-mt-6 [&_h2]:tw-tracking-[-.0375rem] [&_p]:tw-text-[#64748b] [&_p]:tw-text-[.875rem] [&_p]:tw-leading-[1.5rem] [&_p]:tw-m-0 [&_img]:tw-max-h-[4rem] [&_img]:tw-object-contain auth-header"}>
          <AuthOrgBrandingLogo orgBranding={orgBranding} />
          <h2>Create an Account</h2>
          <p>Fill in your details to get started</p>
        </div>

        <form onSubmit={handleSubmit} className={"[&_.ui-button-primary]:tw-mt-[var(--spacing-xs)] [&_.ui-button-primary]:tw-mb-[var(--spacing-xs)] tw-flex tw-flex-col tw-gap-[var(--spacing-md)] auth-form"}>
          <div className={"tw-relative tw-flex tw-flex-col tw-gap-1.5 tw-min-h-[4.5rem] tw-mb-4 [&_label]:tw-text-[#344054] [&_label]:tw-text-[.875rem] [&_label]:tw-font-medium [&_label]:tw-leading-[1.25rem] auth-form-group"}>
            <label htmlFor="name">Full Name</label>
            <div className="tw-relative tw-flex tw-items-center auth-input-wrapper">
              <Icon icon="mdi:account-outline" className={"tw-absolute tw-left-[.875rem] tw-text-[#667085] tw-text-[1.25rem] [[data-theme='dark']_&]:tw-text-[rgba(255,_255,_255,_0.5)] auth-input-icon"} />
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                className={`[&.error:focus]:tw-border-[var(--error-border)] [&.error:focus]:tw-shadow-[0_0_0_4px_rgba(253,_162,_155,_0.1)] [&::placeholder]:tw-text-[#667085] [[data-theme='dark']_&::placeholder]:tw-text-[var(--text-light)] [[data-theme='dark']_&]:[background:rgba(255,_255,_255,_0.05)] [[data-theme='dark']_&]:tw-border-[rgba(255,_255,_255,_0.1)] [[data-theme='dark']_&]:tw-text-[white] [[data-theme='dark']_&:focus]:[background:rgba(255,_255,_255,_0.08)] [[data-theme='dark']_&:focus]:tw-border-[#4361ee] tw-w-full tw-h-[2.75rem] tw-p-[.625rem_.875rem_.625rem_2.625rem] tw-border-[1px] tw-border-solid tw-border-[#dbe1eb] tw-rounded-[.625rem] tw-bg-[#fff] tw-text-[1rem] tw-leading-[1.5rem] tw-text-[#101828] [transition:all_0.15s_ease-in-out] tw-min-h-[2.875rem] [font-family:inherit] [&:focus]:tw-border-[#5265e8] [&:focus]:tw-shadow-[0_0_0_3px_#eef2ff] [&:focus]:[outline:none] [&.error]:tw-border-[#fbbf24] [&.error]:tw-shadow-[0_0_0_4px_rgba(253,_162,_155,_0.1)] [&.error]:tw-bg-[#fffbeb] auth-input   ${errors.name ? "error" : ""}`}
                disabled={isLoading}
              />
            </div>
            {errors.name && <span className="tw-text-[#92400e] tw-text-[.75rem] tw-mt-[4px] tw-block auth-error-message">{errors.name[0]}</span>}
          </div>

          <div className={"tw-relative tw-flex tw-flex-col tw-gap-1.5 tw-min-h-[4.5rem] tw-mb-4 [&_label]:tw-text-[#344054] [&_label]:tw-text-[.875rem] [&_label]:tw-font-medium [&_label]:tw-leading-[1.25rem] auth-form-group"}>
            <label htmlFor="email">Email Address</label>
            <div className="tw-relative tw-flex tw-items-center auth-input-wrapper">
              <Icon icon="mdi:email-outline" className={"tw-absolute tw-left-[.875rem] tw-text-[#667085] tw-text-[1.25rem] [[data-theme='dark']_&]:tw-text-[rgba(255,_255,_255,_0.5)] auth-input-icon"} />
              <input
                type="text"
                id="email"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="example@email.com"
                className={`[&.error:focus]:tw-border-[var(--error-border)] [&.error:focus]:tw-shadow-[0_0_0_4px_rgba(253,_162,_155,_0.1)] [&::placeholder]:tw-text-[#667085] [[data-theme='dark']_&::placeholder]:tw-text-[var(--text-light)] [[data-theme='dark']_&]:[background:rgba(255,_255,_255,_0.05)] [[data-theme='dark']_&]:tw-border-[rgba(255,_255,_255,_0.1)] [[data-theme='dark']_&]:tw-text-[white] [[data-theme='dark']_&:focus]:[background:rgba(255,_255,_255,_0.08)] [[data-theme='dark']_&:focus]:tw-border-[#4361ee] tw-w-full tw-h-[2.75rem] tw-p-[.625rem_.875rem_.625rem_2.625rem] tw-border-[1px] tw-border-solid tw-border-[#dbe1eb] tw-rounded-[.625rem] tw-bg-[#fff] tw-text-[1rem] tw-leading-[1.5rem] tw-text-[#101828] [transition:all_0.15s_ease-in-out] tw-min-h-[2.875rem] [font-family:inherit] [&:focus]:tw-border-[#5265e8] [&:focus]:tw-shadow-[0_0_0_3px_#eef2ff] [&:focus]:[outline:none] [&.error]:tw-border-[#fbbf24] [&.error]:tw-shadow-[0_0_0_4px_rgba(253,_162,_155,_0.1)] [&.error]:tw-bg-[#fffbeb] auth-input   ${errors.email ? "error" : ""}`}
                disabled={isLoading}
              />
            </div>
            {errors.email && <span className="tw-text-[#92400e] tw-text-[.75rem] tw-mt-[4px] tw-block auth-error-message">{errors.email[0]}</span>}
          </div>

          <div className={"tw-relative tw-flex tw-flex-col tw-gap-1.5 tw-min-h-[4.5rem] tw-mb-4 [&_label]:tw-text-[#344054] [&_label]:tw-text-[.875rem] [&_label]:tw-font-medium [&_label]:tw-leading-[1.25rem] auth-form-group"}>
            <label htmlFor="phone">Phone Number</label>
            <div className="tw-flex tw-gap-2">
              <div className="tw-relative tw-flex tw-items-center auth-input-wrapper country-code-wrapper" ref={dropdownRef}>
                <div className={"tw-flex tw-items-center tw-justify-between tw-w-full tw-min-w-[90px] tw-h-[44px] tw-p-[10px_14px] tw-border-[1px] tw-border-solid tw-border-[var(--input-border)] tw-rounded-[8px] tw-bg-[#FFFFFF] tw-text-[16px] tw-leading-[24px] tw-text-[#101828] tw-cursor-pointer [transition:all_var(--transition-fast)] [&:hover]:tw-border-[var(--primary-color)] [&:focus]:tw-border-[var(--primary-color)] [&:focus]:tw-shadow-[0_0_0_4px_rgba(67,_24,_255,_0.1)] [&:focus]:[outline:none] country-code-select"} onClick={() => setShowCountryDropdown(!showCountryDropdown)}>
                  <span>{selectedCountryCode}</span>
                  <Icon icon="mdi:chevron-down" className="tw-text-[#667085] tw-text-[16px] [transition:transform_0.2s_ease] dropdown-icon" />
                </div>

                {showCountryDropdown && (
                  <div className="tw-absolute tw-top-[100%] tw-left-0 tw-z-[1000] tw-w-[300px] tw-max-h-[300px] tw-border-[1px] tw-border-solid tw-border-[#e0e0e0] tw-rounded-[8px] tw-shadow-[0_4px_12px_rgba(0,_0,_0,_0.1)] tw-mt-[4px] tw-bg-[white] tw-right-0 country-dropdown">
                    <div className={"[&_.auth-input-icon]:tw-absolute [&_.auth-input-icon]:tw-left-4 [&_.auth-input-icon]:tw-top-[50%] [&_.auth-input-icon]:[transform:translateY(-50%)] [&_.auth-input-icon]:tw-text-[#6c757d] tw-relative tw-p-[var(--spacing-md)] tw-border-b-[1px] tw-border-solid tw-border-b-[var(--border-color)] [&_input]:tw-w-full [&_input]:tw-p-[var(--spacing-sm)_var(--spacing-sm)_var(--spacing-sm)_var(--spacing-xl)] [&_input]:tw-pl-8 [&_input]:tw-text-[0.875rem] [&_input]:tw-border-[1px] [&_input]:tw-border-solid [&_input]:tw-border-[var(--border-color)] [&_input]:tw-rounded-[var(--radius-md)] [&_input]:tw-bg-[var(--bg-secondary)] [&_input]:tw-text-[var(--text-primary)] [&_input:focus]:[outline:none] [&_input:focus]:tw-border-[var(--primary-color)] [&_input:focus]:tw-bg-[var(--bg-primary)] country-search"}>
                      <SearchInput
                        type="text"
                        placeholder="Search country..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                    <div className={"[&::-webkit-scrollbar]:tw-w-[6px] [&::-webkit-scrollbar-track]:tw-bg-[#f1f1f1] [&::-webkit-scrollbar-thumb]:tw-bg-[#888] [&::-webkit-scrollbar-thumb]:tw-rounded-[3px] [&::-webkit-scrollbar-thumb:hover]:tw-bg-[#555] tw-max-h-[210px] tw-overflow-y-auto country-list"}>
                      {filteredCountries.map((country, index) => (
                        <div
                          key={index}
                          className={"tw-flex tw-items-center tw-justify-between tw-p-[var(--spacing-sm)_var(--spacing-md)] tw-cursor-pointer tw-text-[0.875rem] tw-text-[var(--text-primary)] [&:hover]:tw-bg-[var(--bg-secondary)] country-item"}
                          onClick={() => handleCountrySelect(country)}
                        >
                          <span>{getCountryDisplayName(country)}</span>
                          <span className="tw-text-[#6c757d] country-code">{country.code}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="tw-relative tw-flex tw-items-center auth-input-wrapper tw-grow">
                <Icon icon="mdi:phone-outline" className={"tw-absolute tw-left-[.875rem] tw-text-[#667085] tw-text-[1.25rem] [[data-theme='dark']_&]:tw-text-[rgba(255,_255,_255,_0.5)] auth-input-icon"} />
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={phoneLengthPlaceholder(phoneRule)}
                  maxLength={phoneRule.max}
                  className={`[&.error:focus]:tw-border-[var(--error-border)] [&.error:focus]:tw-shadow-[0_0_0_4px_rgba(253,_162,_155,_0.1)] [&::placeholder]:tw-text-[#667085] [[data-theme='dark']_&::placeholder]:tw-text-[var(--text-light)] [[data-theme='dark']_&]:[background:rgba(255,_255,_255,_0.05)] [[data-theme='dark']_&]:tw-border-[rgba(255,_255,_255,_0.1)] [[data-theme='dark']_&]:tw-text-[white] [[data-theme='dark']_&:focus]:[background:rgba(255,_255,_255,_0.08)] [[data-theme='dark']_&:focus]:tw-border-[#4361ee] tw-w-full tw-h-[2.75rem] tw-p-[.625rem_.875rem_.625rem_2.625rem] tw-border-[1px] tw-border-solid tw-border-[#dbe1eb] tw-rounded-[.625rem] tw-bg-[#fff] tw-text-[1rem] tw-leading-[1.5rem] tw-text-[#101828] [transition:all_0.15s_ease-in-out] tw-min-h-[2.875rem] [font-family:inherit] [&:focus]:tw-border-[#5265e8] [&:focus]:tw-shadow-[0_0_0_3px_#eef2ff] [&:focus]:[outline:none] [&.error]:tw-border-[#fbbf24] [&.error]:tw-shadow-[0_0_0_4px_rgba(253,_162,_155,_0.1)] [&.error]:tw-bg-[#fffbeb] auth-input   ${errors.phone ? "error" : ""}`}
                  disabled={isLoading}
                />
              </div>
            </div>
            {errors.phone?.length ? (
              <span className="tw-text-[#92400e] tw-text-[.75rem] tw-mt-[4px] tw-block auth-error-message">{errors.phone[0]}</span>
            ) : (
              <div className="!tw-text-[var(--secondary-color)] tw-mt-1 tw-block tw-text-[0.75rem] tw-leading-[1rem] [--tw-text-opacity:1] ui-help tw-text-slate-500">
                {`${selectedCountryCode}: ${phoneLengthHintSuffix(phoneRule)}`}
              </div>
            )}
          </div>

          <div className={"tw-relative tw-flex tw-flex-col tw-gap-1.5 tw-min-h-[4.5rem] tw-mb-4 [&_label]:tw-text-[#344054] [&_label]:tw-text-[.875rem] [&_label]:tw-font-medium [&_label]:tw-leading-[1.25rem] auth-form-group"}>
            <label htmlFor="password">Password</label>
            <div className="tw-relative tw-flex tw-items-center auth-input-wrapper">
              <Icon icon="mdi:lock-outline" className={"tw-absolute tw-left-[.875rem] tw-text-[#667085] tw-text-[1.25rem] [[data-theme='dark']_&]:tw-text-[rgba(255,_255,_255,_0.5)] auth-input-icon"} />
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••"
                className={`[&.error:focus]:tw-border-[var(--error-border)] [&.error:focus]:tw-shadow-[0_0_0_4px_rgba(253,_162,_155,_0.1)] [&::placeholder]:tw-text-[#667085] [[data-theme='dark']_&::placeholder]:tw-text-[var(--text-light)] [[data-theme='dark']_&]:[background:rgba(255,_255,_255,_0.05)] [[data-theme='dark']_&]:tw-border-[rgba(255,_255,_255,_0.1)] [[data-theme='dark']_&]:tw-text-[white] [[data-theme='dark']_&:focus]:[background:rgba(255,_255,_255,_0.08)] [[data-theme='dark']_&:focus]:tw-border-[#4361ee] tw-w-full tw-h-[2.75rem] tw-p-[.625rem_.875rem_.625rem_2.625rem] tw-border-[1px] tw-border-solid tw-border-[#dbe1eb] tw-rounded-[.625rem] tw-bg-[#fff] tw-text-[1rem] tw-leading-[1.5rem] tw-text-[#101828] [transition:all_0.15s_ease-in-out] tw-min-h-[2.875rem] [font-family:inherit] [&:focus]:tw-border-[#5265e8] [&:focus]:tw-shadow-[0_0_0_3px_#eef2ff] [&:focus]:[outline:none] [&.error]:tw-border-[#fbbf24] [&.error]:tw-shadow-[0_0_0_4px_rgba(253,_162,_155,_0.1)] [&.error]:tw-bg-[#fffbeb] auth-input   ${errors.password ? "error" : ""}`}
                disabled={isLoading}
              />
              <button
                type="button"
                className={"tw-absolute tw-right-[.875rem] tw-bg-[none] tw-border-0 tw-p-0 tw-text-[#667085] tw-cursor-pointer tw-flex tw-items-center tw-justify-center [&:hover]:tw-text-[#101828] [[data-theme='dark']_&]:tw-text-[var(--text-light)] [[data-theme='dark']_&:hover]:tw-text-[var(--text-primary)] auth-password-toggle"}
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                disabled={isLoading}
              >
                <Icon icon={showPassword ? "mdi:eye-off-outline" : "mdi:eye-outline"} />
              </button>
            </div>
            {errors.password && <span className="tw-text-[#92400e] tw-text-[.75rem] tw-mt-[4px] tw-block auth-error-message">{errors.password[0]}</span>}

            {/* Password Requirements */}
            <div className="tw-flex tw-flex-wrap tw-gap-[8px] tw-mt-[8px] max-[480px]:tw-gap-[6px] password-requirements">
              <div className={`tw-inline-flex tw-items-center tw-p-[4px_12px] tw-bg-[#F2F4F7] tw-rounded-[16px] tw-text-[12px] tw-text-[#667085] [transition:all_0.2s_ease] [&.valid]:tw-bg-[#ECFDF3] [&.valid]:tw-text-[#027A48] [&_.icon]:tw-mr-[4px] [&_.icon]:tw-text-[14px] [&.valid_.icon]:tw-text-[#12B76A] [[data-theme='dark']_&]:tw-bg-[#2D3748] [[data-theme='dark']_&]:tw-text-[#A0AEC0] [[data-theme='dark']_&.valid]:tw-bg-[#1C4532] [[data-theme='dark']_&.valid]:tw-text-[#48BB78] max-[480px]:tw-p-[3px_10px] max-[480px]:tw-text-[11px] requirement-badge   ${passwordRequirements.length ? "valid" : ""}`}>
                <Icon icon={passwordRequirements.length ? "mdi:check" : "mdi:close"} className="icon" />
                8+ Characters
              </div>
              <div className={`tw-inline-flex tw-items-center tw-p-[4px_12px] tw-bg-[#F2F4F7] tw-rounded-[16px] tw-text-[12px] tw-text-[#667085] [transition:all_0.2s_ease] [&.valid]:tw-bg-[#ECFDF3] [&.valid]:tw-text-[#027A48] [&_.icon]:tw-mr-[4px] [&_.icon]:tw-text-[14px] [&.valid_.icon]:tw-text-[#12B76A] [[data-theme='dark']_&]:tw-bg-[#2D3748] [[data-theme='dark']_&]:tw-text-[#A0AEC0] [[data-theme='dark']_&.valid]:tw-bg-[#1C4532] [[data-theme='dark']_&.valid]:tw-text-[#48BB78] max-[480px]:tw-p-[3px_10px] max-[480px]:tw-text-[11px] requirement-badge   ${passwordRequirements.letters ? "valid" : ""}`}>
                <Icon icon={passwordRequirements.letters ? "mdi:check" : "mdi:close"} className="icon" />
                Letters
              </div>
              <div className={`tw-inline-flex tw-items-center tw-p-[4px_12px] tw-bg-[#F2F4F7] tw-rounded-[16px] tw-text-[12px] tw-text-[#667085] [transition:all_0.2s_ease] [&.valid]:tw-bg-[#ECFDF3] [&.valid]:tw-text-[#027A48] [&_.icon]:tw-mr-[4px] [&_.icon]:tw-text-[14px] [&.valid_.icon]:tw-text-[#12B76A] [[data-theme='dark']_&]:tw-bg-[#2D3748] [[data-theme='dark']_&]:tw-text-[#A0AEC0] [[data-theme='dark']_&.valid]:tw-bg-[#1C4532] [[data-theme='dark']_&.valid]:tw-text-[#48BB78] max-[480px]:tw-p-[3px_10px] max-[480px]:tw-text-[11px] requirement-badge   ${passwordRequirements.numbers ? "valid" : ""}`}>
                <Icon icon={passwordRequirements.numbers ? "mdi:check" : "mdi:close"} className="icon" />
                Numbers
              </div>
            </div>
          </div>

          <div className={"tw-relative tw-flex tw-flex-col tw-gap-1.5 tw-min-h-[4.5rem] tw-mb-4 [&_label]:tw-text-[#344054] [&_label]:tw-text-[.875rem] [&_label]:tw-font-medium [&_label]:tw-leading-[1.25rem] auth-form-group"}>
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="tw-relative tw-flex tw-items-center auth-input-wrapper">
              <Icon icon="mdi:lock-outline" className={"tw-absolute tw-left-[.875rem] tw-text-[#667085] tw-text-[1.25rem] [[data-theme='dark']_&]:tw-text-[rgba(255,_255,_255,_0.5)] auth-input-icon"} />
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••"
                className={`[&.error:focus]:tw-border-[var(--error-border)] [&.error:focus]:tw-shadow-[0_0_0_4px_rgba(253,_162,_155,_0.1)] [&::placeholder]:tw-text-[#667085] [[data-theme='dark']_&::placeholder]:tw-text-[var(--text-light)] [[data-theme='dark']_&]:[background:rgba(255,_255,_255,_0.05)] [[data-theme='dark']_&]:tw-border-[rgba(255,_255,_255,_0.1)] [[data-theme='dark']_&]:tw-text-[white] [[data-theme='dark']_&:focus]:[background:rgba(255,_255,_255,_0.08)] [[data-theme='dark']_&:focus]:tw-border-[#4361ee] tw-w-full tw-h-[2.75rem] tw-p-[.625rem_.875rem_.625rem_2.625rem] tw-border-[1px] tw-border-solid tw-border-[#dbe1eb] tw-rounded-[.625rem] tw-bg-[#fff] tw-text-[1rem] tw-leading-[1.5rem] tw-text-[#101828] [transition:all_0.15s_ease-in-out] tw-min-h-[2.875rem] [font-family:inherit] [&:focus]:tw-border-[#5265e8] [&:focus]:tw-shadow-[0_0_0_3px_#eef2ff] [&:focus]:[outline:none] [&.error]:tw-border-[#fbbf24] [&.error]:tw-shadow-[0_0_0_4px_rgba(253,_162,_155,_0.1)] [&.error]:tw-bg-[#fffbeb] auth-input   ${errors.confirmPassword ? "error" : ""}`}
                disabled={isLoading}
              />
              <button
                type="button"
                className={"tw-absolute tw-right-[.875rem] tw-bg-[none] tw-border-0 tw-p-0 tw-text-[#667085] tw-cursor-pointer tw-flex tw-items-center tw-justify-center [&:hover]:tw-text-[#101828] [[data-theme='dark']_&]:tw-text-[var(--text-light)] [[data-theme='dark']_&:hover]:tw-text-[var(--text-primary)] auth-password-toggle"}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                disabled={isLoading}
              >
                <Icon icon={showConfirmPassword ? "mdi:eye-off-outline" : "mdi:eye-outline"} />
              </button>
            </div>
            {errors.confirmPassword && <span className="tw-text-[#92400e] tw-text-[.75rem] tw-mt-[4px] tw-block auth-error-message">{errors.confirmPassword[0]}</span>}
          </div>

          <div className={"tw-relative tw-flex tw-flex-col tw-gap-1.5 tw-min-h-[4.5rem] tw-mb-4 [&_label]:tw-text-[#344054] [&_label]:tw-text-[.875rem] [&_label]:tw-font-medium [&_label]:tw-leading-[1.25rem] auth-form-group"}>
            <label>Interests</label>
            <p className="!tw-text-[var(--secondary-color)] tw-mt-1 tw-block tw-text-[0.75rem] tw-leading-[1rem] [--tw-text-opacity:1] ui-help tw-text-slate-500 tw-mb-2">Choose topics for your course recommendations.</p>
            {isLoadingInterests ? (
              <div className="!tw-text-[var(--secondary-color)] tw-mt-1 tw-block tw-text-[0.75rem] tw-leading-[1rem] [--tw-text-opacity:1] ui-help tw-text-slate-500">Loading interests...</div>
            ) : interestCategories.length > 0 ? (
              <div className="tw-grid tw-grid-cols-[repeat(2,_minmax(0,_1fr))] tw-gap-[8px] tw-max-h-[220px] tw-overflow-y-auto max-[576px]:tw-grid-cols-[1fr] registration-interest-grid">
                {interestCategories.map((category) => {
                  const categoryId = String(category.id);
                  const isSelected = selectedInterestIds.includes(categoryId);
                  return (
                    <label
                      key={categoryId}
                      className={`tw-flex tw-items-center tw-gap-[8px] tw-p-[9px_10px] tw-border-[1px] tw-border-solid tw-border-[#d9dee7] tw-rounded-[8px] tw-cursor-pointer tw-font-normal [&.selected]:tw-border-[#0d6efd] [&.selected]:tw-bg-[#eef5ff] [&_input]:[flex:0_0_auto] registration-interest-option   ${isSelected ? "selected" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleInterest(categoryId)}
                        disabled={isLoading}
                      />
                      <span>{category.name}</span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="!tw-text-[var(--secondary-color)] tw-mt-1 tw-block tw-text-[0.75rem] tw-leading-[1rem] [--tw-text-opacity:1] ui-help tw-text-slate-500">No course interests are available yet.</div>
            )}
            {errors.interests && <span className="tw-text-[#92400e] tw-text-[.75rem] tw-mt-[4px] tw-block auth-error-message">{errors.interests[0]}</span>}
          </div>

          <button
            type="submit"
            className={`[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary   ${isLoading ? "loading" : ""}`}
            disabled={isLoading || isLoadingInterests}
          >
            {isLoading ? (
              <>
                <div className="tw-w-[20px] tw-h-[20px] tw-border-[2px] tw-border-solid tw-border-[rgba(255,_255,_255,_0.3)] tw-rounded-[50%] tw-border-t-[#fff] tw-animate-[spin_0.8s_linear_infinite] tw-mr-[8px] tw-inline-block spinner"></div>
                Creating Account...
              </>
            ) : (
              'Create Account'
            )}
          </button>

          <div className={"tw-mt-[var(--spacing-xs)] tw-text-center tw-text-[#667085] tw-text-[.875rem] tw-leading-[1.25rem] [&_a]:tw-text-[var(--primary-color)] [&_a]:tw-no-underline [&_a]:tw-font-medium [[data-theme='dark']_&]:tw-text-[rgba(255,_255,_255,_0.6)] [[data-theme='dark']_&_a]:tw-text-[#4895ef] auth-options"}>
            Already have an account? <a href="/auth/login" className={isLoading ? "tw-opacity-[0.6] tw-pointer-events-none disabled-link" : ""}>Login</a>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Register() {
  if (!isFeedFeatureEnabled()) {
    return <Navigate to="/auth/login" replace />;
  }

  return <RegisterForm />;
}
